import { Link } from '@/components/LocalizedLink';
import { getToolById } from '@/data/tools';
import { getLocalizedTool } from '@/data/toolsLocale';
import { getProjectCostBandRows, getProjectCostProfileLabel, type ProjectCostProfile } from '@/lib/projectCostEstimate';
import {
  AUTOMATION_EXAMPLES_COPY,
  AUTOMATION_TOOL_EXAMPLES,
  DELIVERY_TERMS_ANCHOR,
  DELIVERY_TERMS_COPY,
  DELIVERY_TERM_FACTORS,
  HANDOVER_ITEMS,
  PROPOSAL_PROCESS_ANCHOR,
  PROPOSAL_PROCESS_COPY,
  PROPOSAL_PROCESS_STEPS,
  SERVICE_COVERAGE_COPY,
  type ProposalLang,
} from '@/lib/proposalProcess';

const linkClass = 'text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors';

type ProposalProcessBlockProps = {
  lang: ProposalLang;
  variant?: 'full' | 'compact';
  className?: string;
};

export function ProposalProcessBlock({ lang, variant = 'full', className = '' }: ProposalProcessBlockProps) {
  const compact = variant === 'compact';
  return (
    <section
      id={PROPOSAL_PROCESS_ANCHOR}
      data-testid="proposal-process"
      className={`scroll-mt-28 rounded-2xl border border-border/70 p-6 lg:p-8 bg-card/40 ${className}`}
    >
      <p className="font-body text-xs tracking-[0.25em] uppercase text-muted-foreground mb-3">
        {PROPOSAL_PROCESS_COPY.tag[lang]}
      </p>
      <h2 className="font-display text-2xl lg:text-3xl text-foreground mb-3">{PROPOSAL_PROCESS_COPY.title[lang]}</h2>
      <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-3xl mb-6">
        {compact ? PROPOSAL_PROCESS_COPY.compactIntro[lang] : PROPOSAL_PROCESS_COPY.intro[lang]}
      </p>
      <ServiceCoverageNote lang={lang} className="mb-6" />
      <ol className={`grid grid-cols-1 gap-4 ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
        {PROPOSAL_PROCESS_STEPS.map((step, index) => (
          <li key={step.title.en} className="rounded-xl border border-border/50 p-4 bg-background">
            <span className="font-display text-[10px] tracking-[0.25em] text-muted-foreground/60 uppercase block mb-2">
              {String(index + 1).padStart(2, '0')}
            </span>
            <h3 className="font-display text-lg text-foreground mb-1">{step.title[lang]}</h3>
            {compact ? null : (
              <p className="font-body text-sm text-muted-foreground leading-relaxed">{step.description[lang]}</p>
            )}
          </li>
        ))}
      </ol>
      <p className="font-body text-sm text-muted-foreground mt-6">
        <Link to="/tools/project-cost-estimator" className={linkClass}>
          {PROPOSAL_PROCESS_COPY.estimatorLink[lang]}
        </Link>
        {compact ? (
          <>
            {' · '}
            <Link to={{ pathname: '/services', hash: `#${PROPOSAL_PROCESS_ANCHOR}` }} className={linkClass}>
              {PROPOSAL_PROCESS_COPY.processLink[lang]}
            </Link>
          </>
        ) : null}
      </p>
    </section>
  );
}

export function ServiceCoverageNote({ lang, className = '' }: { lang: ProposalLang; className?: string }) {
  return (
    <div data-testid="service-coverage" className={`max-w-3xl ${className}`}>
      <h3 className="font-display text-lg text-foreground mb-1">{SERVICE_COVERAGE_COPY.title[lang]}</h3>
      <p className="font-body text-sm text-muted-foreground leading-relaxed">{SERVICE_COVERAGE_COPY.body[lang]}</p>
    </div>
  );
}

type DeliveryTermsBlockProps = {
  lang: ProposalLang;
  profile?: ProjectCostProfile | null;
  /** Pages without the proposal process block still state coverage once. */
  showCoverage?: boolean;
  className?: string;
};

export function DeliveryTermsBlock({ lang, profile, showCoverage = false, className = '' }: DeliveryTermsBlockProps) {
  const bandRows = profile ? getProjectCostBandRows(profile, lang) : [];
  return (
    <section
      id={DELIVERY_TERMS_ANCHOR}
      data-testid="delivery-terms"
      className={`scroll-mt-28 rounded-2xl border border-border/70 p-6 lg:p-8 bg-card/40 ${className}`}
    >
      <h2 className="font-display text-2xl lg:text-3xl text-foreground mb-3">{DELIVERY_TERMS_COPY.title[lang]}</h2>
      <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-3xl mb-6">
        {DELIVERY_TERMS_COPY.intro[lang]}
      </p>
      {showCoverage ? <ServiceCoverageNote lang={lang} className="mb-6" /> : null}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <article>
          <h3 className="font-display text-lg text-foreground mb-2">{DELIVERY_TERMS_COPY.factorsTitle[lang]}</h3>
          <ul className="space-y-2">
            {DELIVERY_TERM_FACTORS.map((item) => (
              <li key={item.en} className="font-body text-sm text-muted-foreground">• {item[lang]}</li>
            ))}
          </ul>
        </article>
        <article>
          <h3 className="font-display text-lg text-foreground mb-2">{DELIVERY_TERMS_COPY.handoverTitle[lang]}</h3>
          <p className="font-body text-sm text-muted-foreground mb-2">{DELIVERY_TERMS_COPY.handoverIntro[lang]}</p>
          <ul className="space-y-2">
            {HANDOVER_ITEMS.map((item) => (
              <li key={item.en} className="font-body text-sm text-muted-foreground">• {item[lang]}</li>
            ))}
          </ul>
        </article>
      </div>
      {profile && bandRows.length > 0 ? (
        <article className="mt-6">
          <h3 className="font-display text-lg text-foreground mb-2">{DELIVERY_TERMS_COPY.bandsTitle[lang]}</h3>
          <p className="font-body text-sm text-muted-foreground mb-2">
            {DELIVERY_TERMS_COPY.bandsIntro[lang]} {getProjectCostProfileLabel(profile, lang)}.
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {bandRows.map((row) => (
              <li key={row.scopeLabel} className="font-body text-sm text-muted-foreground">
                • {row.scopeLabel}: {row.range} ({row.duration})
              </li>
            ))}
          </ul>
        </article>
      ) : null}
      <p className="font-body text-sm text-muted-foreground leading-relaxed mt-6">
        {DELIVERY_TERMS_COPY.supportNote[lang]}{' '}
        <Link to={{ pathname: '/services', hash: `#${PROPOSAL_PROCESS_ANCHOR}` }} className={linkClass}>
          {PROPOSAL_PROCESS_COPY.processLink[lang]}
        </Link>
      </p>
    </section>
  );
}

type AutomationToolExamplesBlockProps = {
  lang: ProposalLang;
  className?: string;
};

export function AutomationToolExamplesBlock({ lang, className = '' }: AutomationToolExamplesBlockProps) {
  const examples = AUTOMATION_TOOL_EXAMPLES.flatMap((example) => {
    const tool = getToolById(example.toolId);
    return tool ? [{ example, title: getLocalizedTool(tool, lang).title }] : [];
  });
  if (!examples.length) return null;
  const copy = AUTOMATION_EXAMPLES_COPY;
  return (
    <section data-testid="automation-tool-examples" className={`rounded-2xl border border-border/70 p-6 lg:p-8 bg-card/40 ${className}`}>
      <h2 className="font-display text-2xl lg:text-3xl text-foreground mb-3">{copy.title[lang]}</h2>
      <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-3xl mb-6">{copy.intro[lang]}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {examples.map(({ example, title }) => (
          <article key={example.toolId} className="rounded-xl border border-border/50 p-5 bg-background">
            <h3 className="font-display text-lg text-foreground mb-3">
              <Link to={`/tools/${example.toolId}`} className="hover:underline underline-offset-4">
                {title}
              </Link>
            </h3>
            <dl className="space-y-2 font-body text-sm">
              {(
                [
                  [copy.automatesLabel, example.automates],
                  [copy.inputsLabel, example.inputs],
                  [copy.outputsLabel, example.outputs],
                  [copy.flowLabel, example.flow],
                  [copy.safeguardsLabel, example.safeguards],
                ] as const
              ).map(([label, value]) => (
                <div key={label.en}>
                  <dt className="text-foreground/80 font-medium">{label[lang]}</dt>
                  <dd className="text-muted-foreground leading-relaxed">{value[lang]}</dd>
                </div>
              ))}
            </dl>
            <Link to={`/tools/${example.toolId}`} className={`inline-block mt-4 font-body text-sm ${linkClass}`}>
              {copy.openTool[lang]} →
            </Link>
          </article>
        ))}
      </div>
    </section>
  );
}
